import type {ComponentPropsWithoutRef} from 'react';
import {forwardRef} from 'react';
import {Slot} from '@radix-ui/react-slot';

import {cn} from '@/modules/foundation/shared/tailwind';

type HeadingProps = ComponentPropsWithoutRef<'h1'> & {
	/** Heading level. This will translate into any h1-6 level unless `asChild` is defined */
	level?: 1 | 2 | 3 | 4 | 5 | 6;
	/** Changes text sizing
	 * @default 'xlarge'
	 */
	size?: 'xlarge' | '2xlarge' | '3xlarge' | '4xlarge';
	/** Adds margin-bottom */
	spacing?: boolean;
	/**
	 * Change the default rendered element for the one passed as a child, merging their props and behavior.
	 * @default false
	 */
	asChild?: boolean;
};

/** Use `Heading` to render h1-6 elements with heading text styles.  */
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
	({level = 1, size = '4xlarge', spacing = false, className, asChild, ...rest}, ref) => {
		const Component = asChild ? Slot : `h${level ?? 1}`;

		return (
			<Component
				ref={ref}
				className={cn([
					// size === 'small' && ['text-base'],
					// size === 'medium' && ['text-lg'], // h6
					// size === 'large' && ['text-xl'], // h5
					size === 'xlarge' && ['text-2xl'], // h4
					size === '2xlarge' && ['text-lg'], // h3
					size === '3xlarge' && ['text-3xl'], // h2
					size === '4xlarge' && ['text-4xl', 'xl:text-5xl'], // h1
					spacing && [
						// size === 'small' && ['mb-3'],
						// size === 'medium' && ['mb-3'],
						// size === 'large' && ['mb-3'],
						size === 'xlarge' && ['mb-3'],
						size === '2xlarge' && ['mb-3'],
						size === '3xlarge' && ['mb-3'],
						size === '4xlarge' && ['mb-4'],
					],
					'font-semibold',
					className,
				])}
				{...rest}
			/>
		);
	},
);

Heading.displayName = 'Heading';
